import { useEffect, useState } from 'react';
import { ApiError, api, type AiStatus, type Identification, type IdCandidate } from '../api.ts';
import { Icon } from './Icon.tsx';

/**
 * Версии о том, что за минерал на снимке.
 *
 * Ровно то, чего здесь нет, и делает блок полезным: он не пишет ничего в
 * карточку сам и не называет ответ определением. По фотографии минерал не
 * определяется — в кадре нет ни твёрдости, ни черты, ни плотности, — поэтому
 * каждая версия приходит с домашней проверкой, а выбор остаётся за
 * владельцем.
 */
export function Identify({
  specimenId,
  hasPhotos,
  onAccepted,
}: {
  specimenId: string;
  hasPhotos: boolean;
  onAccepted: () => void;
}) {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [history, setHistory] = useState<Identification[]>([]);
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .aiStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    void api
      .identifications(specimenId)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [specimenId]);

  // Ключа на сервере нет — функции не существует, и рассказывать о ней
  // в карточке незачем.
  if (!status?.configured) return null;

  const latest = history.find((row) => row.status === 'ok') ?? null;

  async function ask() {
    setBusy(true);
    setError(null);
    try {
      if (!status?.enabled) await api.updateProfile({ ai_enabled: true });
      const result = await api.identify(specimenId, hint.trim() || null);
      setHistory((rows) => [result, ...rows]);
      setStatus((s) => (s ? { ...s, enabled: true } : s));
      setOpen(false);
      setHint('');
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Не получилось спросить');
    } finally {
      setBusy(false);
    }
  }

  async function accept(
    identification: Identification,
    index: number,
    kind: 'candidate' | 'other' = 'candidate',
  ) {
    setBusy(true);
    setError(null);
    try {
      await api.acceptIdentification(identification.id, index, kind);
      // Спутник ничего не вытесняет, поэтому версия не считается принятой:
      // главный минерал по-прежнему можно выбрать.
      if (kind === 'candidate') {
        setHistory((rows) =>
          rows.map((row) =>
            row.id === identification.id
              ? { ...row, accepted_at: new Date().toISOString() }
              : row,
          ),
        );
      }
      onAccepted();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Не получилось принять');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="guess">
      <div className="guess-head">
        <Icon name="book" className="icon muted" />
        <div className="label-key">Что это может быть</div>
        <div className="spacer" />
        {!open && (
          <button
            type="button"
            className="chip chip--plain"
            disabled={!hasPhotos || busy}
            onClick={() => setOpen(true)}
          >
            {latest ? 'спросить ещё раз' : 'предположить'}
          </button>
        )}
      </div>

      {!hasPhotos && (
        <p className="hint">Сначала нужны снимки: судить будут по ним.</p>
      )}

      {open && (
        <div className="guess-ask">
          {!status.enabled && (
            <p className="hint">
              Снимки уйдут на чужой сервер — всё остальное в druzza никуда не
              отправляется. Уходят до трёх фотографий, размеры, вес и название
              места. Цена и точные координаты находки не уходят.
            </p>
          )}
          <div className="label-key">Что вы знаете сами</div>
          <input
            className="field-input"
            value={hint}
            onChange={(event) => setHint(event.target.value)}
            placeholder="царапает стекло, лёгкий, липнет к магниту…"
          />
          <p className="hint">
            Твёрдость, вес в руке, реакция на уксус — то, чего не видно на
            фотографии, и что сильнее всего сужает список.
          </p>
          <div className="chips">
            <button type="button" className="btn btn--primary" disabled={busy} onClick={ask}>
              {busy ? 'думает…' : status.enabled ? 'Спросить' : 'Разрешить и спросить'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Отмена
            </button>
          </div>
          {busy && <p className="hint">Обычно это полминуты.</p>}
        </div>
      )}

      {error && <p className="notice notice--danger">{error}</p>}

      {latest && (
        <div className="guess-result">
          {latest.candidates.map((candidate, index) => (
            <CandidateCard
              key={`${candidate.name}-${index}`}
              candidate={candidate}
              disabled={busy || Boolean(latest.accepted_at)}
              onAccept={() => accept(latest, index)}
            />
          ))}

          {latest.others?.length > 0 && (
            <div className="guess-others">
              <div className="label-key">Что ещё видно на образце</div>
              <p className="hint">
                Это не версии, а спутники: они не спорят с главным минералом
                и записываются рядом с ним.
              </p>
              {latest.others.map((other, index) => (
                <div className="guess-card guess-card--other" key={`${other.name}-${index}`}>
                  <div className="guess-card-head">
                    <div>
                      <div className="guess-name">{other.name_ru ?? other.name}</div>
                      <div className="hint">
                        {other.name_ru ? other.name : 'нет в справочнике'}
                        {other.formula ? ` · ${other.formula}` : ''}
                      </div>
                    </div>
                    <div className="guess-confidence">{Math.round(other.confidence * 100)}%</div>
                  </div>
                  {other.why && <p className="guess-line">{other.why}</p>}
                  <div className="chips">
                    <button
                      type="button"
                      className="chip chip--plain"
                      disabled={busy}
                      onClick={() => accept(latest, index, 'other')}
                    >
                      добавить к образцу
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {latest.missing && (
            <p className="hint guess-missing">
              <Icon name="camera" className="icon icon-xs" /> {latest.missing}
            </p>
          )}

          <p className="hint guess-source">
            {latest.accepted_at ? 'версия принята · ' : ''}
            {latest.model}
            {latest.elapsed_ms ? ` · ${Math.round(latest.elapsed_ms / 1000)} с` : ''}
            {history.length > 1 ? ` · попыток: ${history.length}` : ''}
          </p>

          {/* Обещание «цена и координаты не уходят» стоит ровно столько,
              сколько стоит возможность это проверить. */}
          {latest.context?.facts && (
            <details className="guess-sent">
              <summary className="hint">что именно ушло наружу</summary>
              <pre className="notes">{latest.context.facts}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function CandidateCard({
  candidate,
  disabled,
  onAccept,
}: {
  candidate: IdCandidate;
  disabled: boolean;
  onAccept: () => void;
}) {
  const title = candidate.name_ru ?? candidate.name;
  const percent = Math.round(candidate.confidence * 100);

  return (
    <div className="guess-card">
      <div className="guess-card-head">
        <div>
          <div className="guess-name">{title}</div>
          <div className="hint">
            {candidate.name_ru ? `${candidate.name}` : 'нет в справочнике'}
            {candidate.formula ? ` · ${candidate.formula}` : ''}
            {candidate.hardness ? ` · тв. ${candidate.hardness}` : ''}
          </div>
        </div>
        <div className="guess-confidence" title="Насколько модель в этом уверена">
          {percent}%
        </div>
      </div>

      {candidate.in_locality && (
        <div className="chips">
          <span className="chip chip--ok">встречается на этом месторождении</span>
        </div>
      )}

      {candidate.why && (
        <p className="guess-line">
          <span className="guess-key">за</span> {candidate.why}
        </p>
      )}
      {candidate.distinguish && (
        <p className="guess-line">
          <span className="guess-key">отличие</span> {candidate.distinguish}
        </p>
      )}
      {candidate.test && (
        <p className="guess-line guess-line--test">
          <span className="guess-key">проверка</span> {candidate.test}
        </p>
      )}

      <div className="chips">
        <button type="button" className="chip chip--plain" disabled={disabled} onClick={onAccept}>
          записать как неуверенное определение
        </button>
      </div>
    </div>
  );
}
